import React, { useMemo, useState } from 'react';
import {
  STATUSES,
  STATUS_MAP,
  formatPhone,
  formatDateTime,
  timeAgo,
  callerKey,
  REPEAT_ALERT_THRESHOLD,
} from './constants';

// Roll individual calls up into one row per caller, newest activity first,
// with repeat-caller alerts floated to the top.
function buildGroups(calls) {
  const map = new Map();
  for (const c of calls) {
    const key = callerKey(c);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(c);
  }

  const groups = [];
  for (const [key, list] of map.entries()) {
    list.sort(
      (a, b) =>
        new Date(a.started_at || a.created_at) - new Date(b.started_at || b.created_at)
    );
    const latest = list[list.length - 1];
    const mostRecentWith = (field) => {
      for (let i = list.length - 1; i >= 0; i--) if (list[i][field]) return list[i][field];
      return null;
    };
    // A repeat caller's calls can capture different name detail ("Melinda" on
    // one call, "Melinda Cernocky" on another). Show the fullest one: most
    // words, then longest.
    const fullestName = () => {
      let best = null;
      for (const c of list) {
        const nm = (c.caller_name || '').trim();
        if (!nm) continue;
        if (!best) {
          best = nm;
          continue;
        }
        const words = (s) => s.split(/\s+/).length;
        if (words(nm) > words(best) || (words(nm) === words(best) && nm.length > best.length)) {
          best = nm;
        }
      }
      return best;
    };
    const count = list.length;
    const status = latest.status;
    const resolved = status === 'resolved';
    // Repeat-caller severity — 2 calls = yellow, 3+ = red. Stays lit until the
    // caller is actually marked Resolved.
    const level = resolved
      ? 'none'
      : count >= REPEAT_ALERT_THRESHOLD
      ? 'red'
      : count >= 2
      ? 'yellow'
      : 'none';
    groups.push({
      key,
      list,
      ids: list.map((c) => c.id),
      count,
      status,
      resolved,
      level,
      isNew: status === 'new',
      caller_name:
        fullestName() || formatPhone(latest.callback_number || latest.caller_number),
      callback_number: latest.callback_number || latest.caller_number,
      move_number: mostRecentWith('move_number'),
      preferred_callback_time: mostRecentWith('preferred_callback_time'),
      notes: mostRecentWith('notes'),
      secondary_job_number: mostRecentWith('secondary_job_number'),
      summary: latest.summary,
      assignee_id: latest.assignee_id,
      latestTime: latest.started_at || latest.created_at,
    });
  }

  // Order: red repeats first, then yellow, then normal, resolved last; newest first within each.
  const rank = (g) => (g.resolved ? 0 : g.level === 'red' ? 3 : g.level === 'yellow' ? 2 : 1);
  groups.sort((a, b) => {
    const r = rank(b) - rank(a);
    if (r !== 0) return r;
    return new Date(b.latestTime) - new Date(a.latestTime);
  });
  return groups;
}

export default function CallInbox({ calls, staff, onUpdateGroup }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const groups = useMemo(() => buildGroups(calls), [calls]);
  const colCount = 7;

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className="ui-table-wrap">
      <table className="ui-table">
        <thead>
          <tr>
            <th className="col-caller">Name</th>
            <th className="col-job">Job #</th>
            <th className="col-issue">Issue</th>
            <th className="col-callback">Callback</th>
            <th className="col-status">Status</th>
            <th className="col-assignee">Assignee</th>
            <th className="col-time">Date &amp; time</th>
          </tr>
        </thead>
        <tbody>
          {groups.length === 0 && (
            <tr>
              <td colSpan={colCount} className="ui-empty">
                No calls yet. New calls will appear here automatically.
              </td>
            </tr>
          )}

          {groups.map((g) => {
            const isOpen = expanded.has(g.key);

            return (
              <React.Fragment key={g.key}>
                <tr
                  className={`ui-row${
                    g.level === 'red' ? ' repeat-red' : g.level === 'yellow' ? ' repeat-yellow' : ''
                  }${g.resolved ? ' resolved' : ''}${g.isNew ? ' is-new' : ''}`}
                >
                  {/* Caller */}
                  <td className="col-caller">
                    <div className="caller-cell">
                      <button
                        className="disclosure"
                        onClick={() => toggle(g.key)}
                        aria-label="expand"
                      >
                        {isOpen ? '▾' : '▸'}
                      </button>
                      <div className="caller-main">
                        <div className="caller-name">
                          {g.isNew && <span className="unread-dot" />}
                          <span className="caller-name-text">{g.caller_name}</span>
                          {g.count > 1 && (
                            <span
                              className={`count-badge${
                                g.level === 'red' ? ' red' : g.level === 'yellow' ? ' yellow' : ''
                              }`}
                            >
                              {g.level === 'red' ? '⚠ ' : ''}
                              {g.count} calls
                            </span>
                          )}
                        </div>
                        {g.notes && (
                          <div className="caller-sub">
                            <span className="note-flag" title={g.notes}>📝 Note</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Job # */}
                  <td className="col-job">
                    {g.move_number ? (
                      <span className="job-cell">{g.move_number}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  {/* Issue (click to expand for the full text) */}
                  <td className="col-issue">
                    <button className="issue-text" onClick={() => toggle(g.key)} title="Click to expand">
                      {g.summary || '—'}
                    </button>
                    {g.preferred_callback_time && (
                      <div className="issue-pref">⏱ {g.preferred_callback_time}</div>
                    )}
                  </td>

                  {/* Callback */}
                  <td className="col-callback">
                    {g.callback_number ? (
                      <a href={`tel:${g.callback_number}`} className="callback-link">
                        {formatPhone(g.callback_number)}
                      </a>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="col-status">
                    <span className="status-pill" style={{ '--pill': STATUS_MAP[g.status]?.color || '#999' }}>
                      <select
                        className="ui-select pill-select"
                        value={g.status}
                        onChange={(e) => onUpdateGroup(g.ids, { status: e.target.value })}
                      >
                        {STATUSES.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </span>
                  </td>

                  {/* Assignee */}
                  <td className="col-assignee">
                    <select
                      className="ui-select"
                      value={g.assignee_id || ''}
                      onChange={(e) => onUpdateGroup(g.ids, { assignee_id: e.target.value || null })}
                    >
                      <option value="">Unassigned</option>
                      {staff.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.full_name || s.email}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Date & time stamp */}
                  <td className="col-time">
                    <span className="time-stamp">{formatDateTime(g.latestTime)}</span>
                    <span className="time-rel">{timeAgo(g.latestTime)}</span>
                  </td>
                </tr>

                {/* Expanded detail panel */}
                {isOpen && (
                  <tr className="detail-row">
                    <td colSpan={colCount}>
                      <div className="detail">
                        {/* Quick actions: job numbers + notes */}
                        <div className="detail-actions">
                          <div className="da-row">
                            <div className="da-field">
                              <label>Job / Move #</label>
                              <input
                                className="job-input"
                                defaultValue={g.move_number || ''}
                                placeholder="Add job #"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.currentTarget.blur();
                                }}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v !== (g.move_number || '')) {
                                    onUpdateGroup(g.ids, { move_number: v || null });
                                  }
                                }}
                              />
                            </div>
                            <div className="da-field">
                              <label>Secondary Job #</label>
                              <input
                                className="job-input"
                                defaultValue={g.secondary_job_number || ''}
                                placeholder="Add secondary job #"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.currentTarget.blur();
                                }}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v !== (g.secondary_job_number || '')) {
                                    onUpdateGroup(g.ids, { secondary_job_number: v || null });
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="da-field da-notes">
                            <label>Notes</label>
                            <textarea
                              className="notes-input"
                              defaultValue={g.notes || ''}
                              placeholder="Add a note for this caller…"
                              rows={2}
                              onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== (g.notes || '')) {
                                  onUpdateGroup(g.ids, { notes: v || null });
                                }
                              }}
                            />
                          </div>
                        </div>

                        {/* Full message history — each call as a message */}
                        <div className="thread">
                          {g.list.map((c, i) => (
                            <div key={c.id} className="thread-msg">
                              <div className="thread-rail">
                                <span
                                  className="thread-dot"
                                  style={{ background: STATUS_MAP[c.status]?.color || '#999' }}
                                />
                                {i < g.list.length - 1 && <span className="thread-line" />}
                              </div>
                              <div className="thread-body">
                                <div className="thread-top">
                                  <span className="thread-num">
                                    {g.count > 1 ? `Call ${i + 1}` : 'Call'}
                                  </span>
                                  <span className="thread-time">
                                    {formatDateTime(c.started_at || c.created_at)}
                                  </span>
                                  {c.status === 'new' && <span className="thread-new">new</span>}
                                </div>
                                <div className="thread-summary">{c.summary || '—'}</div>
                                <div className="thread-meta">
                                  {c.move_number && <span>Job {c.move_number}</span>}
                                  {c.preferred_callback_time && (
                                    <span>Wants callback: {c.preferred_callback_time}</span>
                                  )}
                                  {c.callback_number && (
                                    <span>Callback: {formatPhone(c.callback_number)}</span>
                                  )}
                                </div>
                                {c.transcript && (
                                  <details className="transcript">
                                    <summary>Full transcript</summary>
                                    <pre>{c.transcript}</pre>
                                  </details>
                                )}
                                {c.recording_url && (
                                  <a
                                    className="rec-link"
                                    href={c.recording_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    ▶ Recording
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
